import { useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, Megaphone, Phone, History, Settings,
  Menu, X, CalendarDays, Search, Bell, ChevronDown,
  LogOut, User, Moon, Maximize, ChevronRight, Wallet, ShieldCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";

const navSections = [
  {
    label: "MENÜ",
    items: [
      { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    ]
  },
  {
    label: "VERWALTUNG",
    items: [
      { path: "/dashboard/contacts", icon: Users, label: "Kontakte" },
      { path: "/dashboard/campaigns", icon: Megaphone, label: "Kampagnen" },
    ]
  },
  {
    label: "KOMMUNIKATION",
    items: [
      { path: "/dashboard/calls", icon: Phone, label: "Anrufe" },
      { path: "/dashboard/appointments", icon: CalendarDays, label: "Termine" },
      { path: "/dashboard/history", icon: History, label: "Historie" },
      { path: "/dashboard/kosten", icon: Wallet, label: "Kosten" },
    ]
  },
];

export default function DashboardLayout({ user, onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const sections = [
    ...navSections,
    {
      label: "KONTO",
      items: [
        { path: "/dashboard/my-settings", icon: Settings, label: "Einstellungen" },
      ]
    },
    ...(user?.role === "admin" ? [
      {
        label: "ADMIN",
        items: [
          { path: "/dashboard/settings", icon: ShieldCheck, label: "System (Admin)" },
          { path: "/dashboard/admin", icon: ShieldCheck, label: "Admin-Bereich" },
        ]
      }
    ] : [])
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="bg-white shadow-md"
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/30 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ===== SIDEBAR ===== */}
      <aside
        className={`fixed left-0 top-0 bottom-0 w-[250px] bg-white border-r border-[#e8e8e8] z-40 transition-transform duration-300 overflow-y-auto ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex items-center justify-center px-4 py-4 border-b border-[#e8e8e8]">
          <img src="/logo.png" alt="quofon.de" className="h-16 object-contain" />
        </div>

        {/* Navigation Sections */}
        <div className="px-3 py-4 space-y-5">
          {sections.map((section) => (
            <div key={section.label}>
              <p className="text-[10px] font-bold text-[#919aa3] uppercase tracking-[0.12em] px-2 mb-2">
                {section.label}
              </p>
              <nav className="space-y-0.5">
                {section.items.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-2.5 px-3 py-[9px] rounded text-[13px] font-medium transition-all ${
                        isActive
                          ? "bg-[#405189] text-white shadow-sm"
                          : "text-[#495057] hover:text-[#405189] hover:bg-[#eaeaea]"
                      }`}
                      data-testid={`nav-${item.path === "/" ? "dashboard" : item.path.slice(1)}`}
                    >
                      <item.icon className="w-[16px] h-[16px]" />
                      <span className="flex-1">{item.label}</span>
                      {!isActive && <ChevronRight className="w-3 h-3 opacity-30" />}
                    </NavLink>
                  );
                })}
              </nav>
            </div>
          ))}
        </div>

        {/* Sidebar Footer / User */}
        <div className="absolute bottom-0 left-0 right-0 px-4 py-4 border-t border-[#e8e8e8] bg-white">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-[#405189] flex items-center justify-center text-white text-xs font-bold">
              {user?.name?.charAt(0).toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-[#495057] truncate">{user?.name || "Benutzer"}</p>
              <p className="text-[10px] text-[#919aa3] truncate">{user?.email || ""}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* ===== MAIN AREA ===== */}
      <div className="lg:ml-[250px]">
        {/* Top Header */}
        <header className="sticky top-0 z-20 bg-white shadow-sm">
          <div className="flex items-center justify-between px-5 py-3">
            {/* Search */}
            <div className="hidden md:flex items-center flex-1 max-w-sm">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  placeholder="Suchen..."
                  className="pl-9 h-8 text-xs bg-[#f3f6f9] border-slate-200 focus:bg-white"
                  data-testid="header-search"
                />
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-1 ml-auto">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => document.documentElement.requestFullscreen?.()}
                className="hidden md:flex h-8 w-8 text-slate-500 hover:text-slate-700"
              >
                <Maximize className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-slate-700">
                <Moon className="w-4 h-4" />
              </Button>

              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative h-8 w-8 text-slate-500 hover:text-slate-700">
                    <Bell className="w-4 h-4" />
                    <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-red-500 rounded-full" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  <DropdownMenuLabel className="text-xs">Benachrichtigungen</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <div className="py-4 text-center text-xs text-slate-500">Keine neuen Benachrichtigungen</div>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-2 h-8">
                    <div className="w-7 h-7 rounded-full bg-[#405189] flex items-center justify-center text-white text-[10px] font-bold">
                      {user?.name?.charAt(0).toUpperCase() || "U"}
                    </div>
                    <div className="hidden md:block text-left">
                      <p className="text-xs font-semibold text-slate-700 leading-none">{user?.name || "Benutzer"}</p>
                      <p className="text-[10px] text-slate-500 leading-tight mt-0.5">{user?.role === "admin" ? "Administrator" : "Benutzer"}</p>
                    </div>
                    <ChevronDown className="w-3 h-3 text-slate-400 hidden md:block" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel className="text-xs">Mein Konto</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer text-xs">
                    <User className="w-3.5 h-3.5 mr-2" />
                    Profil
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer text-xs" onClick={() => window.location.href = "/dashboard/settings"}>
                    <Settings className="w-3.5 h-3.5 mr-2" />
                    Einstellungen
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="cursor-pointer text-xs text-red-600 focus:text-red-600"
                    onClick={onLogout}
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-3.5 h-3.5 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-5">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
